import { useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';

import { getPropValue } from './utils';

function Chart(props) {
    const [chartType, setChartType] = useState('bar');
    const [selectedLabels, setSelectedLabels] = useState({});
    const [axisData, setAxisData] = useState([]);
    const [series, setSeries] = useState([]);

    useEffect(() => {
        let selectedLabels = {};
        props.series.forEach((s, i) => { selectedLabels['l' + i] = true });
        setSelectedLabels(selectedLabels);
    }, [props.series]);

    useEffect(() => {
        setChartType(props.chartType);
    }, [props.chartType])

    useEffect(() => {
        if (!selectedLabels || !props.axisData || !props.series) return;
        if (Object.entries(selectedLabels).length === 0) {
            setAxisData(props.axisData);
            setSeries(props.series);
            return;
        }
        let series = [];
        let i = -1;
        for (const [, value] of Object.entries(selectedLabels)) {
            i++;
            if (!value) continue;
            if (chartType === 'pie') {
                let data = props.series[i].data.map((s, idx) => {
                    return {
                        id: idx,
                        value: s,
                        label: props.axisData[idx]
                    }
                });
                if (props.angleOnPie) {
                    series.push({
                        data, innerRadius: 34,
                        outerRadius: 113,
                        paddingAngle: 3,
                        cornerRadius: 5,
                        startAngle: -25,
                        endAngle: 360
                    });
                } else {
                    series.push({
                        data, innerRadius: 4,
                        outerRadius: 113,
                        paddingAngle: 1,
                        cornerRadius: 5,
                        startAngle: -56,
                        endAngle: 360
                    });
                }
                break;
            }
            series.push(props.series[i]);
        }
        if (chartType === 'pie' && series.length === 0) {
            series.push({
                data: []
            });
        }
        setAxisData(props.axisData);
        setSeries(series);
    }, [chartType, props.axisData, props.series, selectedLabels, props.angleOnPie]);

    const handleChartType = (event, newChartType) => {
        if (newChartType !== null) {
            setChartType(newChartType);
        }
    };

    return (
        <Stack
            direction={{ xs: 'column', xl: 'row' }}
            spacing={1}
            sx={{ width: '600px', height: '300px', }}
        >
            <Box sx={{ flexGrow: 1 }}>
                <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartType}
                    aria-label="chart type"
                    fullWidth
                >
                    {['bar', 'line', 'pie'].map((type) => (
                        <ToggleButton size="small" key={type} value={type} aria-label="left aligned">
                            {type}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {chartType === 'bar' && (
                    <BarChart
                        xAxis={[{ scaleType: 'band', data: axisData }]}
                        series={series}
                        onItemClick={(event, d) => {
                            // console.log("Clicked data:", d);
                            // let s = series.find((serie) => serie.data.includes(d));
                            // let x = d.index;
                            // console.log("Selected series:", s, "Index:", x);
                        }}
                        
                    />
                )}
                {chartType === 'line' && (
                    <LineChart
                        xAxis={[{ scaleType: 'band', data: axisData }]}
                        series={series}
                    />
                )}
                {chartType === 'pie' && (
                    <PieChart
                        series={series}
                        margin={{ top: 0, bottom: 0, left: 0, right: 200 }}
                        slotProps={{
                            legend: {
                                direction: 'column',
                                position: { vertical: 'middle', horizontal: 'right' },
                                padding: 0,
                                labelStyle: {
                                    fontSize: 10
                                },
                                itemMarkWidth: 20,
                                itemMarkHeight: 2,
                                markGap: 5,
                                itemGap: 10,
                            },
                        }}
                    />
                )}
            </Box>
            <Stack
                direction={{ xs: 'row', xl: 'column' }}
                spacing={3}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
            >
                <FormGroup>
                    {selectedLabels ? props.series.map(((s, i) => <FormControlLabel
                        control={<Checkbox
                            defaultChecked
                            checked={selectedLabels['l' + i]}
                            indeterminate={false}
                            onChange={(e) => {
                                setSelectedLabels((prev => {
                                    prev = { ...prev };
                                    prev['l' + i] = e.target.checked;
                                    return prev;
                                }))
                            }}
                        />}
                        label={s.label} />)) : null}
                </FormGroup>
            </Stack>
        </Stack>
    );
}

export default function GridDashboard(props) {
    const [charts, setCharts] = useState(null);

    useEffect(() => {
        function createColData(data, dimCol, dataCol) {
            const seriesData = [];
            const axisData = [];
            let lastValue = "";
            let i = -1;
            data.sort((a, b) => {
                let nameA = getPropValue(a, dimCol)?.toUpperCase(); // ignore upper and lowercase
                let nameB = getPropValue(b, dimCol)?.toUpperCase(); // ignore upper and lowercase
                if (dimCol.endsWith("Date")) {//TODO:Date must be checked against data type
                    nameA = new Date(nameA);
                    nameB = new Date(nameB);
                } else {
                    if (!nameA) nameA = " ";
                    if (!nameB) nameB = " ";
                }
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            }).forEach((obj) => {
                let currentValue = "N/A";
                if (getPropValue(obj, dimCol) != null) currentValue = getPropValue(obj, dimCol).toString();
                if (dimCol.endsWith("Date") && currentValue !== "N/A") currentValue = currentValue.substring(0, 10);
                if (lastValue.toString() !== currentValue && lastValue!=="Others") {
                    if (i >= 8 && !dimCol.endsWith("Date")) {
                        currentValue = "Others";
                    }
                    i++;
                    lastValue = currentValue;
                    seriesData.push(0);
                    axisData.push(lastValue);
                }
                if (dataCol === "__cnt__") {
                    seriesData[i] += 1;
                } else {
                    if (getPropValue(obj, dataCol) != null) seriesData[i] += getPropValue(obj, dataCol);
                }
            });
            return { axisData, seriesData };
        }

        function createChartMeta(columns, data) {
            const charts = [];
            columns.forEach(dimCol => {
                if (!dimCol.columnName.endsWith('Aisle') && //TODO: Must be app specific (from the backend)
                    !dimCol.columnName.endsWith('Descr1') &&
                    !dimCol.columnName.endsWith('Descr2') &&
                    !dimCol.columnName.endsWith('Name1') &&
                    !dimCol.columnName.endsWith('Name2') &&
                    !dimCol.columnName.endsWith('Date')
                ) {
                    return;
                }
                let series = [];
                let cntData = createColData(data, dimCol.columnName, "__cnt__");
                const axisData = cntData.axisData;
                series.push({ data: cntData.seriesData, label: "Count" });
                columns.filter((dataCol) => dataCol.columnName.endsWith('Qty')).forEach((dataCol) => {
                    let colData = createColData(data, dimCol.columnName, dataCol.columnName);
                    series.push({ data: colData.seriesData, label: dataCol.caption });
                });
                let defaultChartType = null;
                if (dimCol.columnName.endsWith('Date')) {
                    defaultChartType = 'line';
                } else if (series.length === 1 && axisData.length > 1) {
                    defaultChartType = 'pie';
                } else if (axisData.length === 1) {
                    defaultChartType = 'bar';
                }
                charts.push({ colName: dimCol.columnName, caption: dimCol.caption, axisData, series, defaultChartType });
            });
            setCharts(charts);
        }
        createChartMeta(props.columns, props.data);
    }, [props.columns, props.data]);

    function createChart(chart, chartType, angleOnPie) {
        return <div className="mb-4 me-4">
            <h6>{chart.caption}</h6>
            <Chart chartType={chartType} axisData={chart.axisData} series={chart.series} angleOnPie={angleOnPie} />
        </div>
    }

    function createCharts() {
        if (!charts) return null;
        const charTypes = ['bar', 'pie'];
        let angleOnPie = false;
        return <div style={{ display: 'flex', flexWrap: 'wrap', overflowY: 'scroll', height: '100%', width: '100%' }}>{charts.map((chart, i) => {
            let chartType = chart.defaultChartType;
            if (!chartType) chartType = charTypes[i % charTypes.length];
            if (chartType === 'pie') {
                angleOnPie = !angleOnPie;
            }
            return createChart(chart, chartType, angleOnPie);
        })}</div>
    }

    return createCharts();
}

